import { useState } from "react";
import {
  ActionIcon,
  Badge,
  Button,
  Drawer,
  Group,
  Menu,
  NumberInput,
  Select,
  Stack,
  Switch,
  Table,
  Text,
  Textarea,
  TextInput,
  Title,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { SafeDateInput as DateInput } from "@/components/SafeDateInput";
import { notifications } from "@mantine/notifications";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  IconDots,
  IconPencil,
  IconPlus,
  IconTrash,
} from "@tabler/icons-react";
import dayjs from "dayjs";
import { FormModal } from "@/components/FormModal";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { Can } from "@/components/Can";
import { api } from "@/lib/api";
import { formatAPIError, qk } from "@/lib/apiHelpers";
import type {
  Company,
  CompaniesListResponse,
  Contract,
  ContractCreateRequest,
  ContractUpdateRequest,
  ContractorRole,
} from "@/types/api";

interface ContractFormValues {
  company_id: string;
  role: ContractorRole;
  number: string;
  date: Date | null;
  with_vat: boolean;
  payment_terms: string;
  payment_days: number | string;
  is_active: boolean;
  notes: string;
}

function parseDate(s?: string): Date | null {
  if (!s) return null;
  const d = dayjs(s);
  return d.isValid() ? d.toDate() : null;
}

function formatDate(d: Date | null | undefined): string | undefined {
  if (!d) return undefined;
  return dayjs(d).format("YYYY-MM-DD");
}

function ContractForm({
  form,
  companies,
}: {
  form: ReturnType<typeof useForm<ContractFormValues>>;
  companies: Company[];
}) {
  const companyData = companies.map((co) => ({ value: co.id, label: co.name }));
  return (
    <Stack gap="md">
      <Select
        label="Наша фирма"
        required
        placeholder="Выберите фирму"
        data={companyData}
        searchable
        {...form.getInputProps("company_id")}
      />
      <Select
        label="Роль"
        required
        data={[
          { value: "client", label: "Заказчик" },
          { value: "carrier", label: "Перевозчик" },
        ]}
        {...form.getInputProps("role")}
      />
      <TextInput
        label="Номер договора"
        required
        placeholder="ДОГ-2024-001"
        {...form.getInputProps("number")}
      />
      <DateInput
        label="Дата договора"
        placeholder="дд.мм.гггг"
        valueFormat="DD.MM.YYYY"
        clearable
        {...form.getInputProps("date")}
      />
      <Group grow>
        <Switch
          label="С НДС"
          checked={form.values.with_vat}
          onChange={(e) => form.setFieldValue("with_vat", e.currentTarget.checked)}
        />
        <Switch
          label="Активен"
          checked={form.values.is_active}
          onChange={(e) => form.setFieldValue("is_active", e.currentTarget.checked)}
        />
      </Group>
      <Group grow>
        <TextInput
          label="Условия оплаты"
          placeholder="30 дней после доставки"
          {...form.getInputProps("payment_terms")}
        />
        <NumberInput
          label="Срок оплаты (дней)"
          placeholder="30"
          min={0}
          max={365}
          {...form.getInputProps("payment_days")}
        />
      </Group>
      <Textarea
        label="Примечания"
        autosize
        minRows={2}
        {...form.getInputProps("notes")}
      />
    </Stack>
  );
}

export interface ContractsDrawerProps {
  entityType: "clients" | "carriers";
  entityId: string;
  entityName: string;
  defaultRole: ContractorRole;
  opened: boolean;
  onClose: () => void;
}

export function ContractsDrawer({
  entityType,
  entityId,
  entityName,
  defaultRole,
  opened,
  onClose,
}: ContractsDrawerProps) {
  const qc = useQueryClient();
  const perm = entityType === "clients" ? "clients" : "carriers";

  const { data: contractsRaw, isLoading } = useQuery({
    queryKey: qk.contracts(entityType, entityId),
    queryFn: async () =>
      (await api.get<Contract[]>(`/api/v1/${entityType}/${entityId}/contracts`)).data,
    enabled: opened,
  });
  const contracts = contractsRaw ?? [];

  const { data: companiesRaw } = useQuery({
    queryKey: qk.companies(),
    queryFn: async () =>
      (await api.get<CompaniesListResponse>(`/api/v1/companies?page=1&page_size=200`)).data,
    enabled: opened,
  });
  const companies = companiesRaw?.items ?? [];

  const [editing, setEditing] = useState<Contract | null | undefined>(undefined);
  const [confirmDelete, setConfirmDelete] = useState<Contract | null>(null);

  const form = useForm<ContractFormValues>({
    initialValues: {
      company_id: "",
      role: defaultRole,
      number: "",
      date: null,
      with_vat: false,
      payment_terms: "",
      payment_days: "",
      is_active: true,
      notes: "",
    },
    validate: {
      company_id: (v) => (v ? null : "Обязательно"),
      number: (v) => (v.trim() ? null : "Обязательно"),
    },
  });

  const openCreate = () => {
    form.reset();
    form.setValues({ company_id: "", role: defaultRole, number: "", date: null, with_vat: false, payment_terms: "", payment_days: "", is_active: true, notes: "" });
    setEditing(null);
  };

  const openEdit = (ct: Contract) => {
    form.setValues({
      company_id: ct.company_id,
      role: ct.role as ContractorRole,
      number: ct.number,
      date: parseDate(ct.date),
      with_vat: ct.with_vat,
      payment_terms: ct.payment_terms ?? "",
      payment_days: ct.payment_days ?? "",
      is_active: ct.is_active,
      notes: ct.notes ?? "",
    });
    setEditing(ct);
  };

  const invalidate = () => void qc.invalidateQueries({ queryKey: qk.contracts(entityType, entityId) });

  const createMut = useMutation({
    mutationFn: async (body: ContractCreateRequest) =>
      (await api.post<Contract>(`/api/v1/${entityType}/${entityId}/contracts`, body)).data,
    onSuccess: () => {
      notifications.show({ message: "Договор добавлен", color: "green" });
      invalidate();
      setEditing(undefined);
    },
    onError: (e) => notifications.show({ title: "Ошибка", message: formatAPIError(e), color: "red" }),
  });

  const updateMut = useMutation({
    mutationFn: async ({ cid, body }: { cid: string; body: ContractUpdateRequest }) =>
      (await api.patch<Contract>(`/api/v1/${entityType}/${entityId}/contracts/${cid}`, body)).data,
    onSuccess: () => {
      notifications.show({ message: "Договор обновлён", color: "green" });
      invalidate();
      setEditing(undefined);
    },
    onError: (e) => notifications.show({ title: "Ошибка", message: formatAPIError(e), color: "red" }),
  });

  const deleteMut = useMutation({
    mutationFn: async (cid: string) =>
      (await api.delete(`/api/v1/${entityType}/${entityId}/contracts/${cid}`)).data,
    onSuccess: () => {
      notifications.show({ message: "Договор удалён", color: "green" });
      invalidate();
      setConfirmDelete(null);
    },
    onError: (e) => notifications.show({ title: "Ошибка", message: formatAPIError(e), color: "red" }),
  });

  const submit = () => {
    if (form.validate().hasErrors) return;
    const v = form.values;
    const body: ContractCreateRequest = {
      company_id: v.company_id,
      role: v.role,
      number: v.number,
      date: formatDate(v.date),
      with_vat: v.with_vat,
      payment_terms: v.payment_terms || undefined,
      payment_days: v.payment_days !== "" ? Number(v.payment_days) : undefined,
      is_active: v.is_active,
      notes: v.notes || undefined,
    };
    if (editing) {
      updateMut.mutate({ cid: editing.id, body });
    } else {
      createMut.mutate(body);
    }
  };

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      title={
        <div>
          <Text fw={600}>Договоры</Text>
          <Text size="sm" c="dimmed">{entityName}</Text>
        </div>
      }
      position="right"
      size="lg"
    >
      <Stack gap="md">
        <Group justify="space-between">
          <Title order={4}>Список договоров</Title>
          <Can perm={`${perm}:write`}>
            <Button size="xs" leftSection={<IconPlus size={14} />} onClick={openCreate}>
              Добавить договор
            </Button>
          </Can>
        </Group>

        {isLoading && <Text c="dimmed" size="sm">Загрузка...</Text>}

        {!isLoading && contracts.length === 0 && (
          <Text c="dimmed" size="sm">Договоры не добавлены</Text>
        )}

        {contracts.length > 0 && (
          <Table striped highlightOnHover withTableBorder fz="sm">
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Наша фирма</Table.Th>
                <Table.Th>Роль</Table.Th>
                <Table.Th>Номер</Table.Th>
                <Table.Th>Дата</Table.Th>
                <Table.Th>НДС</Table.Th>
                <Table.Th>Статус</Table.Th>
                <Table.Th w={40} />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {contracts.map((ct) => (
                <Table.Tr key={ct.id}>
                  <Table.Td>{ct.company_name}</Table.Td>
                  <Table.Td>
                    {ct.role === "client" ? "Заказчик" : "Перевозчик"}
                  </Table.Td>
                  <Table.Td>{ct.number}</Table.Td>
                  <Table.Td>
                    {ct.date ? dayjs(ct.date).format("DD.MM.YYYY") : "—"}
                  </Table.Td>
                  <Table.Td>
                    {ct.with_vat ? (
                      <Badge color="blue" variant="light" size="xs">С НДС</Badge>
                    ) : (
                      <Badge color="gray" variant="light" size="xs">Без НДС</Badge>
                    )}
                  </Table.Td>
                  <Table.Td>
                    {ct.is_active ? (
                      <Badge color="green" variant="light" size="xs">Активен</Badge>
                    ) : (
                      <Badge color="gray" variant="light" size="xs">Архив</Badge>
                    )}
                  </Table.Td>
                  <Table.Td>
                    <Can perm={`${perm}:write`}>
                      <Menu position="bottom-end" shadow="md">
                        <Menu.Target>
                          <ActionIcon variant="subtle" size="sm">
                            <IconDots size={14} />
                          </ActionIcon>
                        </Menu.Target>
                        <Menu.Dropdown>
                          <Menu.Item leftSection={<IconPencil size={14} />} onClick={() => openEdit(ct)}>
                            Редактировать
                          </Menu.Item>
                          <Menu.Item
                            leftSection={<IconTrash size={14} />}
                            color="red"
                            onClick={() => setConfirmDelete(ct)}
                          >
                            Удалить
                          </Menu.Item>
                        </Menu.Dropdown>
                      </Menu>
                    </Can>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        )}
      </Stack>

      <FormModal
        opened={editing !== undefined}
        onClose={() => setEditing(undefined)}
        title={editing ? "Редактировать договор" : "Новый договор"}
        onSubmit={submit}
        loading={createMut.isPending || updateMut.isPending}
        size="lg"
      >
        <ContractForm form={form} companies={companies} />
      </FormModal>

      <ConfirmDialog
        opened={!!confirmDelete}
        onClose={() => setConfirmDelete(null)}
        onConfirm={() => confirmDelete && deleteMut.mutate(confirmDelete.id)}
        title="Удалить договор?"
        message={`Договор "${confirmDelete?.number}" будет удалён. Действие необратимо.`}
        loading={deleteMut.isPending}
      />
    </Drawer>
  );
}
